import React from "react";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Chip, Tooltip, Typography } from "@mui/material";
import { DocumentType } from "constants/adminApi";
import {
  ApiKycLogs,
  RequestType,
  VerificationOutcome,
  VerificationStepSummary,
} from "types/admin";

const OUTCOME_COLORS: Record<
  VerificationOutcome,
  "success" | "error" | "default"
> = {
  SUCCESS: "success",
  FAILURE: "error",
  UNKNOWN: "default",
};

const OUTCOME_LABELS: Record<VerificationOutcome, string> = {
  SUCCESS: "Success",
  FAILURE: "Failure",
  UNKNOWN: "Unknown",
};

const OUTCOME_ICONS: Record<VerificationOutcome, React.ReactElement> = {
  SUCCESS: <CheckCircleOutlineIcon fontSize="small" />,
  FAILURE: <CancelOutlinedIcon fontSize="small" />,
  UNKNOWN: <HelpOutlineIcon fontSize="small" />,
};

interface VerificationOutcomeChipProps {
  step?: VerificationStepSummary | null;
  emptyLabel?: string;
}

export function VerificationOutcomeChip({
  step,
  emptyLabel = "—",
}: VerificationOutcomeChipProps) {
  if (!step) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  const outcome = step.outcome ?? "UNKNOWN";
  const tooltip = step.message
    ? `${OUTCOME_LABELS[outcome]}: ${step.message}`
    : OUTCOME_LABELS[outcome];

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        color={OUTCOME_COLORS[outcome]}
        icon={OUTCOME_ICONS[outcome]}
        aria-label={tooltip}
        sx={{
          minWidth: 28,
          "& .MuiChip-label": { display: "none" },
          "& .MuiChip-icon": { m: 0 },
        }}
      />
    </Tooltip>
  );
}

export function formatEstablishmentType(value?: string) {
  if (!value) return "—";
  const normalized = value.trim().toLowerCase().replace(/_/g, " ");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

const ADMIN_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
};

const ISO_DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

function formatWallClockDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
) {
  const wallClock = new Date(Date.UTC(year, month, day, hour, minute, second));
  return new Intl.DateTimeFormat("en-IN", {
    ...ADMIN_DATE_FORMAT_OPTIONS,
    timeZone: "UTC",
  }).format(wallClock);
}

export function formatDate(value?: string) {
  if (!value) return "-";

  const trimmed = value.trim();
  const match = trimmed.match(ISO_DATETIME_RE);
  if (match) {
    const [, year, month, day, hour, minute, second = "00"] = match;
    return formatWallClockDate(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "-";

  return new Intl.DateTimeFormat("en-IN", {
    ...ADMIN_DATE_FORMAT_OPTIONS,
    timeZone: "Asia/Kolkata",
  }).format(parsed);
}

export function kycStatusChipColor(
  status: string
): "default" | "warning" | "info" | "success" {
  switch (status) {
    case "KYC_INITIATED":
      return "warning";
    case "PAN_VERIFIED":
      return "info";
    case "BANK_VERIFIED":
      return "info";
    case "GST_REQUIRED":
    case "GST_VERIFICATION_PENDING":
    case "GST_VERIFIED":
      return "info";
    case "VERIFIED":
      return "success";
    default:
      return "default";
  }
}

export function documentTypeForRequestType(
  requestType?: RequestType
): DocumentType | undefined {
  switch (requestType) {
    case "PAN_VERIFICATION":
    case "PAN_COMPLIANCE":
      return "PAN_CARD_PROOF";
    case "BANK_VERIFICATION":
      return "BANK_ACCOUNT_PROOF";
    default:
      return undefined;
  }
}

export function resolveApiLogDocumentKey(entry: ApiKycLogs): string | undefined {
  const trimmed = entry.documentReference?.trim();
  return trimmed || undefined;
}

export function logOutcomeFromStatus(status?: string): VerificationOutcome {
  const statusCode = Number.parseInt(status ?? "", 10);
  if (Number.isNaN(statusCode)) {
    return "UNKNOWN";
  }
  return statusCode >= 200 && statusCode < 300 ? "SUCCESS" : "FAILURE";
}

export function apiLogToVerificationStep(log?: ApiKycLogs): VerificationStepSummary | undefined {
  if (!log) {
    return undefined;
  }

  const statusCode = Number.parseInt(log.status ?? "", 10);

  return {
    requestType: log.requestType,
    outcome: logOutcomeFromStatus(log.status),
    message: log.message,
    httpStatusCode: Number.isNaN(statusCode) ? undefined : statusCode,
    uploadedDocumentKey: log.documentReference,
    attemptedAt: log.createdOn,
  };
}

export function findLatestApiLog(
  logs: ApiKycLogs[] | undefined,
  requestType: RequestType
): ApiKycLogs | undefined {
  return logs?.find((entry) => entry.requestType === requestType);
}

export function findPrimaryFailureLog(logs: ApiKycLogs[] | undefined): ApiKycLogs | undefined {
  return logs?.find((entry) => logOutcomeFromStatus(entry.status) === "FAILURE");
}

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  PAN_VERIFICATION: "PAN Verification",
  PAN_COMPLIANCE: "PAN Compliance",
  BANK_VERIFICATION: "Bank Verification",
};

export function formatRequestTypeLabel(requestType?: RequestType) {
  if (!requestType) return "Verification";
  return REQUEST_TYPE_LABELS[requestType] ?? requestType;
}

export function formatJsonMap(value?: Record<string, string>) {
  if (!value || Object.keys(value).length === 0) {
    return undefined;
  }
  return JSON.stringify(value, null, 2);
}
